import { isAnyRealEstateCategory } from '@kijiji/category'
import { ResultsView, useGetSearchPageStateQuery } from '@kijiji/generated/graphql-types'
import { useRouter } from 'next/router'
import qs from 'query-string'
import { useCallback } from 'react'

import { getBoundingBoxFromRadius } from '@/features/map/utils/getBoundingBoxFromRadius'
import { updateSearchPageCache } from '@/features/search/utils/updateSearchPageCache'
import { useGetSearchCategory } from '@/hooks/category/useGetSearchCategory'
import { useGetLocation } from '@/hooks/location/useGetLocation'
import { useSearchActions } from '@/hooks/srp'

export const useGetSrpState = () => {
  const { asPath, push } = useRouter()

  const { location } = useGetLocation()
  const { category } = useGetSearchCategory()
  const { refetchResults } = useSearchActions()
  const { data, client } = useGetSearchPageStateQuery({
    fetchPolicy: 'cache-only',
  })

  const handleUpdatePageViewType = useCallback(
    (viewType: ResultsView) => {
      if (viewType === data?.searchState.pageViewType) return

      const { url, query } = qs.parseUrl(asPath)
      const newUrl = qs.stringifyUrl({ url, query: { ...query, view: viewType.toLowerCase() } })

      if (viewType === ResultsView.Map) {
        let boundingBox = undefined

        if (isAnyRealEstateCategory(category.id) && location.area) {
          const { latitude, longitude, radius } = location.area
          boundingBox = getBoundingBoxFromRadius(latitude, longitude, radius)
        }

        /** When refetching results the "push" happens automatically */
        refetchResults({
          view: viewType,
          location: { id: location.id, area: location.area, boundingBox },
        })
      } else {
        // Update route
        push(newUrl, undefined, { scroll: false, shallow: true })
      }

      // Update cache
      updateSearchPageCache(client, {
        __typename: 'SearchPageState',
        currentPage: data?.searchState.currentPage ?? 1,
        pageViewType: viewType,
      })
    },
    [
      asPath,
      category.id,
      client,
      data?.searchState.currentPage,
      data?.searchState.pageViewType,
      location.area,
      location.id,
      push,
      refetchResults,
    ]
  )

  return {
    currentPage: data?.searchState.currentPage ?? 1,
    updatePageViewType: handleUpdatePageViewType,
    viewType: data?.searchState.pageViewType ?? ResultsView.List,
  }
}
